import { useState, useEffect } from "react";
import { StyledContainer } from "./StyledComponents";
import { KmsTypeRelatedMediaEntry } from "../types";
import Details from "./Details";
import {
    durationToHuman,
    getEntryUrl,
    safeSetTimeout,
    ServerTimeHelper,
    translate,
} from "@mediaspace/shared/utils";
import { useDateFormat } from "@mediaspace/hooks";
import { ButtonClickAnalyticsType } from "@mediaspace/shared/types/ButtonClickAnalyticsType";
import { SimpleThumbnail } from "@mediaspace/shared/ui";
import { useButtonAnalytics } from "@mediaspace/hooks";

/**
 * related media item component
 */
const MediaItem = (props: KmsTypeRelatedMediaEntry) => {
    const { id, thumbnailUrl, name, schedulingData, duration } = props;

    const schedulingDataTz = schedulingData?.timezone;
    const startTime = schedulingData?.from ?? 0;
    const endTime = schedulingData?.to ?? 0;

    const {
        startDay,
        endDay,
        formattedScheduledStartTime,
        formattedScheduledEndTime,
    } = useDateFormat(startTime, endTime, schedulingDataTz, true);

    const serverTime = ServerTimeHelper.getUpdatedTime();
    const [isLive, setIsLive] = useState(
        startTime && endTime && serverTime < endTime && serverTime > startTime
    );

    let ariaLabelText = translate("go to media %1", [name]);
    if (startDay && formattedScheduledStartTime && startTime && endTime) {
        if (isLive) {
            ariaLabelText += translate("live now");
        } else {
            ariaLabelText += translate("scheduled to %1 %2", [
                startDay,
                formattedScheduledStartTime,
            ]);
        }
    } else if (duration) {
        ariaLabelText += durationToHuman(duration);
    }

    // set isLive=true based on start time
    useEffect(() => {
        if (!startTime || isLive || serverTime > endTime) {
            return;
        }

        const timeoutID = safeSetTimeout(
            () => setIsLive(true),
            startTime - serverTime
        );

        return () => {
            clearTimeout(timeoutID);
        };
    }, [serverTime, startTime, endTime, isLive]);

    // set isLive=false based on end time
    useEffect(() => {
        if (!endTime || !isLive) {
            return;
        }

        const timeoutID = safeSetTimeout(
            () => setIsLive(false),
            endTime - serverTime
        );

        return () => {
            clearTimeout(timeoutID);
        };
    }, [serverTime, endTime, isLive]);

    const sendButtonAnalytics = useButtonAnalytics();

    const sendAnalytics = () => {
        sendButtonAnalytics("Related media", ButtonClickAnalyticsType.LINK);
    };

    return (
        <StyledContainer
            className={"kms-ds-media-page-sidebar-related-media-item"}
            href={getEntryUrl(id, undefined, name)}
            target={"_self"}
            aria-label={ariaLabelText}
            onClick={sendAnalytics}
        >
            <SimpleThumbnail
                thumbnailUrl={thumbnailUrl}
                name={name}
                isLive={!!isLive ?? false}
                duration={duration}
                classes={{
                    root: "kms-ds-media-page-sidebar-related-media-item-thumb",
                    indicator: isLive ? "kms-ds-media-page-sidebar-related-media-item-live" : "kms-ds-media-page-sidebar-related-media-item-duration"
                }}
            />
            <Details
                name={name}
                startDay={startDay ?? 0}
                endDay={endDay ?? 0}
                startTime={formattedScheduledStartTime ?? 0}
                endTime={formattedScheduledEndTime ?? 0}
            />
        </StyledContainer>
    );
};

export default MediaItem;
